<template>
  <div class="component" @keyup.enter="submit">
    <div class="card">
      <p class="title">Iniciar sesión</p>
      <el-form :model="authentication"
               :rules="rules"
               ref="LoginForm">
        <el-form-item prop="country">
          <el-radio-group v-model="authentication.country"
                          size="mini"
                          class="radio-countries">
            <el-radio-button v-for="(country, index) in countries"
                             :key="index"
                             :label="country.code">
              <Flag :code="country.code"
                    :text="country.name"/>
            </el-radio-button>
          </el-radio-group>
        </el-form-item>
        <el-form-item prop="email">
          <el-input v-model="authentication.email"
                    placeholder="usuario@correo.cl"
                    suffix-icon="far fa-envelope"
                    :disabled="loading">
          </el-input>
        </el-form-item>
        <el-form-item prop="password">
          <el-input v-model="authentication.password"
                    type="password"
                    placeholder="********"
                    suffix-icon="fas fa-key"
                    :disabled="loading">
          </el-input>
        </el-form-item>
      </el-form>
    </div>
    <el-button class="default"
               @click="submit"
               :loading="loading">{{loadingText}}</el-button>
  </div>
</template>

<script>
import Flag from '@/app/components/base/Flag.vue';
import { syncRumUserContext, clearRumUserContext } from '@/observability/datadogRum';
import crud from '../../../mixins/crud';
import utils from '../../../mixins/utils';
import { decodeRSA } from '../../../mixins/function';
import { apiRequest, updateToken } from '../../../backend/vue-axios/axios';

export default {
  name: 'Login',
  components: {
    Flag,
  },
  data() {
    const validateEmail = (rule, value, callback) => {
      if (!value || value === '') {
        callback(new Error('Ingrese un correo electrónico'));
      } else if (!this.validEmail(value)) {
        callback(new Error('El correo es incorrecto'));
      } else {
        callback();
      }
    };
    const validatePassword = (rule, value, callback) => {
      if (!value || value === '') {
        callback(new Error('Ingrese una contraseña'));
      } else {
        callback();
      }
    };
    return {
      authentication: {
        email: '',
        password: '',
        country: 'CL',
      },
      countries: [{
        code: 'CL',
        name: 'Chile',
      }, {
        code: 'PE',
        name: 'Perú',
      }],
      rules: {
        email: [{ validator: validateEmail, trigger: 'blur' }],
        password: [{ validator: validatePassword, trigger: 'blur' }],
      },
      loading: false,
      messageError: [{
        code: 500,
        message: 'Error interno, contacte a soporte.',
      }, {
        code: 503,
        message: 'Servicio no disponible temporalmente.',
      }, {
        code: 504,
        message: 'Lentitud en el acceso, intente más tarde.',
      }, {
        code: 401,
        message: 'Correo o contraseña incorrectos.',
      }],
    };
  },
  methods: {
    setSessionUser(response) {
      sessionStorage.user_email = response.data.email || '';
      sessionStorage.user_name = response.data.name || '';
      sessionStorage.user_last_name = response.data.last_name || '';
      syncRumUserContext();
    },
    submit() {
      this.$refs.LoginForm.validate((valid) => {
        if (valid) this.authenticate();
        return valid;
      });
    },
    validEmail(email) {
      // eslint-disable-next-line
      const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      return re.test(email);
    },
    authenticate() {
      this.loading = true;
      apiRequest({
        module: 'crud',
        service: 'login',
        country: this.authentication.country,
        params: {
          username: this.authentication.email,
          password: this.authentication.password,
        },
      }).then(response => this.onSuccess(response)).catch(err => this.onError(err));
    },
    onSuccess(response) {
      this.loading = true;
      if (!response.data.token) { this.onError(); }
      sessionStorage.expire_token = response.data.expire;
      sessionStorage.token = response.data.token;
      updateToken();
      apiRequest({
        module: 'crud',
        service: 'account_info',
        country: this.authentication.country,
      }).then(res => this.onSuccessInfo(res)).catch(err => this.onError(err));
    },
    onError(err) {
      this.loading = false;
      clearRumUserContext();
      const msgErr = this.messageError.find(msg => msg.code === err.response.status);
      const msg = msgErr ? msgErr.message : 'Error al autentificar, contacte a soporte.';
      this.$message({
        message: msg,
        type: 'error',
        duration: 9000,
      });
      delete sessionStorage.clear();
    },
    onSuccessInfo(res) {
      sessionStorage.type = res.data.account_types_id;
      sessionStorage.country = res.data.country;
      this.setSessionUser(res);
      if (decodeRSA(sessionStorage.type) === '5') {
        const variables = {
          component: this,
          module: 'crud',
          service: 'get_accounts',
          params: {
            accounts_email: res.data.email,
          },
        };
        crud.request(variables).then((response) => {
          this.loading = true;
          utils.reassign(variables, 'service', 'get_carriers');
          utils.reassign(variables, 'params', { carriers_accounts_id: response.id });
          crud.request(variables).then((innerRes) => {
            sessionStorage.enterprise = innerRes.enterprises_id;
            this.loading = false;
            this.$router.replace(this.$route.query.redirect || 'home');
          });
        });
      } else {
        this.$router.replace(this.$route.query.redirect || 'home');
      }
    },
  },
  computed: {
    loadingText() {
      return this.loading ? 'ACCEDIENDO' : 'ACCEDER';
    },
  },
};
</script>

<style lang="scss" scoped>
  .component {
    .card {
      padding: 20px;
      border-radius: 4px;
      margin-bottom: 20px;
      background-color: $fb-grey-light-94;
      .el-form-item:last-child { margin: 10px 10px 15px; }
      .el-form-item { margin: 10px 10px 30px; }
      .title {
        font-size: 30px;
        font-weight: 300;
        margin: 5px 0 30px;
        text-align: center;
        // font-size: calc(1em + 1.8vw);
      }
    }
    .el-button { width: 100%; }
  }
</style>
